import jsPDF from "jspdf";
import {
  PAGE, CONTENT_W, COLORS, text, wrapText, drawHeader, drawFooters,
  drawKpiGrid, checkPageBreak, fmtDate,
} from "./pdfHelpers";
import { humanize, fmtCompact, fmtPercent, fmtNumber } from "./textHelpers";

const TITLE = "Executive Summary";

export function buildExecutiveSummaryPdf(metrics, content, { watermark = true } = {}) {

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const { meta } = metrics;

  // ================= PAGE 1 — EXECUTIVE SNAPSHOT =================
  doc.setFillColor(...COLORS.brand);
  doc.rect(0, 0, PAGE.w, 42, "F");

  doc.setTextColor(255, 255, 255);

  if (watermark) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    text(doc, "InsightPulse AI", PAGE.margin, 16);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  text(doc, TITLE, PAGE.margin, 30);

  doc.setTextColor(...COLORS.text);

  let y = 52;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COLORS.subtext);

  const metaLine = [
    meta.datasetName,
    metrics.timePeriod ? `${metrics.timePeriod.start} - ${metrics.timePeriod.end}` : null,
    fmtDate(meta.generatedAt),
  ].filter(Boolean).join("   |   ");

  text(doc, metaLine, PAGE.margin, y);
  y += 12;

  const kpis = [];

  if (metrics.primary) {
    kpis.push({ label: `Total ${humanize(metrics.primary.measure)}`, value: fmtCompact(metrics.primary.total) });
    kpis.push({ label: `Average ${humanize(metrics.primary.measure)}`, value: fmtCompact(metrics.primary.average) });
    kpis.push({ label: "Records", value: fmtNumber(metrics.primary.count) });
  }

  if (metrics.overallChange) {
    kpis.push({
      label: "Overall Change",
      value: metrics.overallChange.direction === "stable" ? "Stable" : fmtPercent(metrics.overallChange.pctChange),
      direction: metrics.overallChange.direction,
    });
  }

  if (metrics.repPerformance?.rows?.length) {
    kpis.push({ label: `Top ${humanize(metrics.repPerformance.dimension)}`, value: metrics.repPerformance.rows[0].label });
  }

  y = drawKpiGrid(doc, y, kpis, 3) + 10;

  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.4);
  doc.line(PAGE.margin, y, PAGE.w - PAGE.margin, y);
  y += 10;
  doc.setLineWidth(0.2);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.brand);
  text(doc, "Executive Takeaway", PAGE.margin, y);
  y += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...COLORS.text);
  y = wrapText(doc, content.takeaway || "", PAGE.margin, y, CONTENT_W, 6);

  // ================= PAGE 2 — MANAGEMENT PRIORITIES =================
  doc.addPage();
  y = 24;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.text);
  text(doc, "Management Priorities", PAGE.margin, y);
  doc.setDrawColor(...COLORS.brand);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, y + 2, PAGE.margin + 22, y + 2);
  doc.setLineWidth(0.2);
  y += 12;

  (content.priorities || []).forEach((p, i) => {

    y = checkPageBreak(doc, y, 20);

    doc.setFillColor(...COLORS.brand);
    doc.circle(PAGE.margin + 3, y - 1.5, 3.2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    text(doc, String(i + 1), PAGE.margin + 3 - (i + 1 >= 10 ? 2 : 1), y);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...COLORS.text);
    text(doc, p.title, PAGE.margin + 9, y);
    y += 5.5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.subtext);
    y = wrapText(doc, p.explanation, PAGE.margin + 9, y, CONTENT_W - 9, 5) + 6;
  });

  y += 4;

  doc.setDrawColor(...COLORS.border);
  doc.line(PAGE.margin, y, PAGE.w - PAGE.margin, y);
  y += 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.text);
  text(doc, "Management Actions", PAGE.margin, y);
  doc.setDrawColor(...COLORS.brand);
  doc.line(PAGE.margin, y + 2, PAGE.margin + 22, y + 2);
  y += 10;

  (content.actions || []).forEach((a) => {

    y = checkPageBreak(doc, y, 12);

    doc.setFillColor(...COLORS.brand);
    doc.circle(PAGE.margin + 1.5, y - 1.5, 1.2, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    y = wrapText(doc, a, PAGE.margin + 6, y, CONTENT_W - 6, 5.5) + 5;
  });

  drawFooters(doc, TITLE, { watermark });

  doc.setPage(2);
  drawHeader(doc, TITLE, meta.datasetName || "Dataset", { watermark });

  return doc;
}
